/**
 * BookingQRAdmin — Settings → Online Booking + QR Downloads
 *
 * Single source of truth for:
 *   • Booking Page URL + preview
 *   • QR preview (with copy / share / print / download)
 *   • Download format grid (PNG · SVG · A4 · Desk · Card · Sticker)
 *   • Booking rules (slot / buffer / advance / min-notice)
 *   • Pricing visibility
 *   • Booking analytics (scans, bookings, conversion, OTP health, sources, trends)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useClinicConfig } from "@/lib/clinicConfig";
import { toast } from "sonner";
import {
  QrCode, Download, Copy, ExternalLink, Loader2, Settings as SettingsIcon,
  Calendar, Clock, ShieldCheck, Share2, Printer, FileImage,
  FileType2, LayoutTemplate, Sticker as StickerIcon, IdCard, MonitorSmartphone,
  TrendingUp, ShieldAlert, CheckCircle2, MessageSquare,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

// ─── Static metadata for the 6 download formats ──────────────────────
const FORMAT_META = {
  png:          { icon: FileImage,        label: "PNG",              spec: "300 DPI · 800×800", desc: "Universal image — WhatsApp, email, slides." },
  svg:          { icon: FileType2,        label: "SVG",              spec: "Vector · Any size", desc: "Print at any resolution without pixelation." },
  "pdf-a4":     { icon: LayoutTemplate,   label: "A4 Poster",        spec: "PDF · 210×297 mm",  desc: "Waiting-room wall or notice board." },
  "pdf-desk":   { icon: MonitorSmartphone,label: "Reception Desk Stand", spec: "PDF · 148×210 mm", desc: "Foldable A5 counter card at the front desk." },
  "pdf-card":   { icon: IdCard,           label: "Visiting Card",    spec: "PDF · 85×55 mm",    desc: "Include on your visiting cards & referral slips." },
  "pdf-sticker":{ icon: StickerIcon,      label: "Sticker",          spec: "PDF · 60×60 mm",    desc: "Square peel-off for equipment and folders." },
};

export default function BookingQRAdmin() {
  const [info, setInfo]     = useState(null);
  const [clinic, setClinic] = useState(null);
  const [busy, setBusy]     = useState(false);
  const cfg = useClinicConfig();

  const load = useCallback(async () => {
    try {
      const [i, c] = await Promise.all([
        api.get("/clinic/qr/info"),
        api.get("/clinics/me"),
      ]);
      setInfo(i.data);
      setClinic(c.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load booking settings"));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveBookingConfig = async (patch) => {
    try {
      setBusy(true);
      const bc = { ...(clinic?.booking_config || {}), ...patch };
      await api.patch("/clinics/me", { booking_config: bc });
      setClinic({ ...clinic, booking_config: bc });
      toast.success("Saved");
      cfg.invalidateAllTabs?.();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!info || !clinic) {
    return <div className="p-8 text-ink-soft"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const bc = clinic.booking_config || {};

  return (
    <div className="p-1 space-y-8" data-testid="booking-admin-page">
      <div>
        <span className="overline text-ink-soft">Settings</span>
        <h1 className="mt-2 font-display text-4xl tracking-tight flex items-center gap-3">
          <QrCode className="w-8 h-8 text-ink" /> Online Booking &amp; QR
        </h1>
        <p className="mt-2 text-sm text-ink-mid max-w-xl">
          Print this QR once, hand it out forever. Every scan lands the patient on your branded
          booking page and every booking flows straight into your calendar.
        </p>
      </div>

      <QrPreviewCard info={info} clinic={clinic} />
      <DownloadFormatsGrid info={info} />
      <BookingRulesCard bc={bc} busy={busy} onSave={saveBookingConfig} />
      <QrAnalyticsSection />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// QR Preview — logo overlay + share/copy/print/download controls
// ─────────────────────────────────────────────────────────────────────
function QrPreviewCard({ info, clinic }) {
  const url = info.booking_url;
  const logoUrl = clinic.logo_url || clinic.settings?.logo_url || null;

  const copyUrl = () => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied");
  };
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: clinic.name, text: `Book at ${clinic.name}`, url });
      } else {
        copyUrl();
      }
    } catch { /* user dismissed */ }
  };
  const print = () => {
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) return;
    w.document.write(`
      <html><head><title>${clinic.name} — Booking QR</title>
      <style>
        body{margin:0;font-family:system-ui;text-align:center;padding:40px;color:#1a1a1a;}
        h1{font-size:22px;margin:12px 0 4px;}
        p{font-size:12px;color:#555;margin:0 0 24px;letter-spacing:1px;text-transform:uppercase;}
        img.qr{width:360px;height:360px;background:#fff;padding:16px;border:1px solid #eee;border-radius:16px;}
        .url{margin-top:20px;font-family:monospace;font-size:12px;color:#333;}
      </style></head><body>
      <h1>${clinic.name}</h1>
      <p>Scan to book your appointment</p>
      <img class="qr" src="${BASE}/api/clinic/qr.png?size=800" />
      <div class="url">${url}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
             data-testid="qr-preview-card">
      <div className="grid md:grid-cols-[auto_1fr] gap-8">
        <div className="rounded-2xl bg-gradient-to-br from-[#F6F5F1] to-[#EFE9D9] p-6 text-center min-w-[280px]">
          <div className="relative inline-block">
            <img
              src={`${BASE}/api/clinic/qr.png?size=800`}
              alt="Booking QR"
              className="w-60 h-60 rounded-xl bg-white p-3 shadow-md"
              data-testid="qr-preview"
            />
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Clinic logo"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-lg bg-white p-1 shadow-md border border-[#E7E1D3] object-contain"
                data-testid="qr-logo-overlay"
              />
            )}
          </div>
          <div className="mt-4 font-display text-lg text-ink" data-testid="qr-clinic-name">{clinic.name}</div>
          <div className="text-[11px] uppercase tracking-widest text-ink-soft mt-1">
            Scan to book your appointment
          </div>
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-ink-mid bg-white/70 rounded-full px-3 py-1 max-w-full">
            <span className="truncate max-w-[220px]" data-testid="qr-booking-url">{url}</span>
          </div>
        </div>

        {/* Actions — copy · share · print · download */}
        <div className="flex flex-col justify-center gap-3">
          <div className="text-[11px] uppercase tracking-widest text-ink-soft mb-1">Quick actions</div>
          <QuickAction
            icon={Copy} label="Copy booking link" testid="btn-copy-url"
            onClick={copyUrl} />
          <QuickAction
            icon={Share2} label="Share via WhatsApp / SMS" testid="btn-share-url"
            onClick={share} />
          <QuickAction
            icon={Printer} label="Print QR poster" testid="btn-print-qr"
            onClick={print} />
          <a href={`${BASE}${info.formats?.[0]?.url || "/api/clinic/qr.png"}`}
             target="_blank" rel="noopener noreferrer"
             data-testid="btn-download-quick"
             className="inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E7E1D3] bg-white hover:border-pine hover:bg-[#F5F0E3] text-sm text-ink transition-colors">
            <Download className="w-4 h-4 text-ink-mid" /> Download standard PNG
          </a>
          <a href={url} target="_blank" rel="noopener noreferrer"
             data-testid="btn-open-booking-page"
             className="inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E7E1D3] bg-white hover:border-pine hover:bg-[#F5F0E3] text-sm text-ink transition-colors">
            <ExternalLink className="w-4 h-4 text-ink-mid" /> Open booking page in new tab
          </a>
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-ink-soft">
            <ShieldCheck className="w-3.5 h-3.5" />
            This QR is permanent — safe to print on visiting cards &amp; signage.
          </div>
        </div>
      </div>
    </section>
  );
}

function QuickAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className="inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E7E1D3] bg-white hover:border-pine hover:bg-[#F5F0E3] text-sm text-ink transition-colors text-left">
      <Icon className="w-4 h-4 text-ink-mid" /> {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Downloads — 6 consistent cards, equal height/width, hover polish
// ─────────────────────────────────────────────────────────────────────
function DownloadFormatsGrid({ info }) {
  return (
    <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
             data-testid="qr-download-card">
      <div className="flex items-center gap-2 mb-4">
        <Download className="w-4 h-4 text-ink" />
        <div className="text-[11px] uppercase tracking-widest text-ink-mid">Download formats</div>
      </div>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-fr"
           data-testid="download-grid">
        {info.formats.map((f) => {
          const meta = FORMAT_META[f.kind] || {
            icon: Download, label: f.label, spec: "", desc: "",
          };
          const Icon = meta.icon;
          return (
            <div key={f.kind}
                 data-testid={`qr-format-card-${f.kind}`}
                 className="group flex flex-col rounded-2xl border border-[#E7E1D3] bg-white p-5 hover:border-pine hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#F5F0E3] group-hover:bg-pine/10 flex items-center justify-center transition-colors">
                  <Icon className="w-5 h-5 text-pine" />
                </div>
                <span className="text-[9px] uppercase tracking-widest text-ink-soft border border-[#EFEADC] rounded-full px-2 py-0.5">
                  {f.kind.toUpperCase()}
                </span>
              </div>
              <div className="text-base font-medium text-ink" data-testid={`format-name-${f.kind}`}>
                {meta.label}
              </div>
              <div className="text-xs text-ink-mid mt-1 flex-1 leading-relaxed">
                {meta.desc}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-ink-soft"
                   data-testid={`format-spec-${f.kind}`}>
                {meta.spec}
              </div>
              <a href={`${BASE}${f.url}`}
                 target="_blank" rel="noopener noreferrer"
                 data-testid={`qr-format-${f.kind}`}
                 className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-pine text-white text-sm font-medium px-4 py-2.5 hover:bg-pine/90 transition-colors">
                <Download className="w-4 h-4" /> Download
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Booking Rules — unchanged behaviour, refactored for tidiness
// ─────────────────────────────────────────────────────────────────────
function BookingRulesCard({ bc, busy, onSave }) {
  return (
    <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
             data-testid="booking-config-card">
      <div className="flex items-center gap-2 mb-5">
        <SettingsIcon className="w-4 h-4 text-ink" />
        <div className="text-[11px] uppercase tracking-widest text-ink-mid">Online booking rules</div>
      </div>

      <label className="flex items-center justify-between gap-4 p-4 rounded-xl border border-[#EFEADC] cursor-pointer hover:bg-[#FBFAF6]"
             data-testid="toggle-accept-online">
        <div>
          <div className="font-medium text-ink">Accept online bookings</div>
          <div className="text-xs text-ink-mid">Turn off temporarily during holidays or overloaded days.</div>
        </div>
        <input
          type="checkbox"
          checked={bc.accepts_online_bookings !== false}
          onChange={(e) => onSave({ accepts_online_bookings: e.target.checked })}
          className="w-5 h-5 accent-pine"
        />
      </label>

      <label className="mt-3 flex items-center justify-between gap-4 p-4 rounded-xl border border-[#EFEADC] cursor-pointer hover:bg-[#FBFAF6]"
             data-testid="toggle-show-pricing">
        <div>
          <div className="font-medium text-ink">Show pricing on booking page</div>
          <div className="text-xs text-ink-mid">
            When off, treatment fees are replaced with
            &ldquo;Fee will be discussed during consultation&rdquo;. Per-doctor
            visibility can be set from the Team page.
          </div>
        </div>
        <input
          type="checkbox"
          checked={bc.show_pricing !== false}
          onChange={(e) => onSave({ show_pricing: e.target.checked })}
          className="w-5 h-5 accent-pine"
        />
      </label>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <NumberInput
          label="Default slot duration (minutes)"
          icon={Clock}
          value={bc.default_slot_min ?? 30}
          min={10} max={120} step={5}
          onChange={(v) => onSave({ default_slot_min: v })}
          testid="input-slot-min"
        />
        <NumberInput
          label="Buffer between appointments (minutes)"
          icon={Clock}
          value={bc.buffer_min ?? 0}
          min={0} max={30} step={5}
          hint="Time reserved for cleaning / notes between visits"
          onChange={(v) => onSave({ buffer_min: v })}
          testid="input-buffer-min"
        />
        <NumberInput
          label="Advance booking window (days)"
          icon={Calendar}
          value={bc.advance_days ?? 30}
          min={1} max={180} step={1}
          hint="Maximum days ahead a patient can book"
          onChange={(v) => onSave({ advance_days: v })}
          testid="input-advance-days"
        />
        <NumberInput
          label="Minimum notice (minutes)"
          icon={Clock}
          value={bc.min_notice_min ?? 60}
          min={0} max={1440} step={15}
          hint="Nobody can book something starting earlier than this"
          onChange={(v) => onSave({ min_notice_min: v })}
          testid="input-min-notice"
        />
      </div>

      {busy && (
        <div className="mt-3 text-xs text-ink-soft flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Analytics — expanded: OTP health, sources, trends, cancellations
// ─────────────────────────────────────────────────────────────────────
function QrAnalyticsSection() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/clinic/qr/analytics?days=${days}`)
       .then(({ data }) => setData(data))
       .catch(() => setData(null));
  }, [days]);

  const chartMax = useMemo(() => {
    if (!data?.daily?.length) return 1;
    return Math.max(...data.daily.map((r) => r.scans), 1);
  }, [data]);

  if (!data) return null;
  const otp = data.otp || {};
  const src = data.sources || { online: 0, walk_in: 0 };
  const srcTotal = (src.online + src.walk_in) || 1;

  return (
    <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
             data-testid="qr-analytics-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <QrCode className="w-4 h-4 text-ink" />
          <div className="text-[11px] uppercase tracking-widest text-ink-mid">Booking analytics</div>
        </div>
        <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white text-xs">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    data-testid={`qr-range-${d}`}
                    className={`px-3 py-1 rounded-full transition ${
                      days === d ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Top-line metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricPill label="QR scans"          value={data.total_scans} testid="stat-scans" />
        <MetricPill label="Bookings"          value={data.total_bookings} testid="stat-bookings" />
        <MetricPill label="Conversion"        value={`${data.conversion_pct}%`} tone="clay" testid="stat-conversion" />
        <MetricPill label="Peak hour"         value={data.peak_hour_label || "—"} testid="stat-peak" />
      </div>

      {/* OTP health */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricPill
          icon={MessageSquare}
          label="OTP requests"
          value={otp.total_requests || 0}
          testid="stat-otp-total"
        />
        <MetricPill
          icon={CheckCircle2}
          label="OTP delivered"
          value={`${otp.delivery_success_pct || 0}%`}
          tone={otp.delivery_success_pct >= 90 ? "pine" : "clay"}
          testid="stat-otp-delivered"
        />
        <MetricPill
          icon={ShieldCheck}
          label="OTP verified"
          value={`${otp.verify_success_pct || 0}%`}
          tone={otp.verify_success_pct >= 70 ? "pine" : "clay"}
          testid="stat-otp-verified"
        />
        <MetricPill
          icon={ShieldAlert}
          label="Failed attempts"
          value={otp.failed_attempts || 0}
          tone="clay"
          testid="stat-otp-failures"
        />
      </div>

      {/* Sources + Cancellations */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white border border-[#EFEADC] p-4"
             data-testid="stat-source-online">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">Online bookings</div>
          <div className="font-display text-3xl mt-1 text-pine tabular-nums">{src.online}</div>
          <div className="mt-2 text-[11px] text-ink-mid">
            {Math.round((src.online / srcTotal) * 100)}% of all appointments
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-[#EFEADC] p-4"
             data-testid="stat-source-walkin">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">Walk-in / manual</div>
          <div className="font-display text-3xl mt-1 text-ink tabular-nums">{src.walk_in}</div>
          <div className="mt-2 text-[11px] text-ink-mid">
            {Math.round((src.walk_in / srcTotal) * 100)}% of all appointments
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-[#EFEADC] p-4"
             data-testid="stat-cancellations">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">Cancelled / No-show</div>
          <div className="font-display text-3xl mt-1 text-[#B94A48] tabular-nums">{data.cancelled_count || 0}</div>
          <div className="mt-2 text-[11px] text-ink-mid">Last {days} days</div>
        </div>
      </div>

      {/* Daily trend */}
      {data.daily?.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Daily activity
          </div>
          <div className="flex items-end gap-1 h-24" data-testid="qr-daily-chart">
            {data.daily.slice(-30).map((d) => {
              const h = Math.max(4, (d.scans / chartMax) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col justify-end"
                     title={`${d.date}: ${d.scans} scans, ${d.bookings} bookings`}>
                  <div className="bg-pine rounded-t" style={{ height: `${h}%`, minHeight: "2px" }} />
                  {d.bookings > 0 && (
                    <div className="bg-[#D06A42] rounded-b"
                         style={{ height: `${Math.max(2, (d.bookings / chartMax) * 100)}%` }} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex gap-4 text-[10px] text-ink-mid">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-pine rounded-sm" /> Scans</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#D06A42] rounded-sm" /> Bookings</span>
          </div>
        </div>
      )}

      {/* Monthly rollup (only when it's meaningful) */}
      {data.monthly?.length >= 2 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Monthly rollup</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="qr-monthly-chart">
            {data.monthly.map((m) => (
              <div key={m.month}
                   className="rounded-lg border border-[#EFEADC] bg-white p-2 text-center">
                <div className="text-[10px] text-ink-soft">{m.month}</div>
                <div className="text-xs font-medium mt-1 text-pine tabular-nums">{m.scans}s</div>
                <div className="text-xs text-[#D06A42] tabular-nums">{m.bookings}b</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function MetricPill({ label, value, tone = "pine", icon: Icon, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#EFEADC] p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`font-display text-3xl mt-1 tabular-nums ${
        tone === "clay" ? "text-[#D06A42]" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function NumberInput({ label, icon: Icon, value, min, max, step, onChange, hint, testid }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <div>
      <div className="text-xs text-ink-mid mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <input
        type="number" min={min} max={max} step={step} value={local}
        data-testid={testid}
        onChange={(e) => setLocal(Number(e.target.value))}
        onBlur={() => local !== value && onChange(local)}
        className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-pine focus:ring-2 focus:ring-pine/20 tabular-nums"
      />
      {hint && <div className="text-[10px] text-ink-soft mt-1">{hint}</div>}
    </div>
  );
}
